import { Stack } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { useWatch } from 'react-hook-form';
import type { Control, FieldError, FieldErrorsImpl, FieldValues, Merge, Path } from 'react-hook-form';
import { VALIDITY_PERIOD_OPTIONS } from '../../constants';
import DateTimeSelect from './DateTimeSelect';
import FormSelect from './FormSelect';
import { dayjs } from '../../config/dayjs';
import { VALIDITY_PERIOD } from '../../constants/enums';

type DateFieldError = FieldError | Merge<FieldError, FieldErrorsImpl<Dayjs>>;

type Props<T extends FieldValues, TTransformedValues extends FieldValues = T> = {
  control: Control<T, unknown, TTransformedValues>;
  disabled?: boolean;
  fromTimestampError?: DateFieldError;
  required?: boolean;
  toTimestampError?: DateFieldError;
};

const ValidityPeriodSelect = <T extends FieldValues, TTransformedValues extends FieldValues = T>({
  control,
  disabled,
  fromTimestampError,
  required,
  toTimestampError,
}: Props<T, TTransformedValues>) => {
  const [validityPeriod, fromTimestamp] = useWatch({
    control,
    name: ['validityPeriod', 'fromTimestamp'] as [Path<T>, Path<T>],
  });

  const showDateRange = validityPeriod === VALIDITY_PERIOD.CUSTOM;

  return (
    <>
      <FormSelect
        control={control}
        name={'validityPeriod' as Path<T>}
        label='Validity period'
        options={VALIDITY_PERIOD_OPTIONS}
        disabled={disabled}
        allowDeselect={false}
        required={required}
      />
      {showDateRange && (
        <Stack direction='row' spacing={2}>
          <DateTimeSelect
            control={control}
            name={'fromTimestamp' as Path<T>}
            label='From'
            disableTimePicker
            disabled={disabled}
            error={fromTimestampError}
            required={required}
          />
          <DateTimeSelect
            control={control}
            name={'toTimestamp' as Path<T>}
            label='To'
            minDate={fromTimestamp ? dayjs(fromTimestamp).add(1, 'day') : undefined}
            disableTimePicker
            disabled={disabled}
            error={toTimestampError}
            required={required}
          />
        </Stack>
      )}
    </>
  );
};

export default ValidityPeriodSelect;
